import { useEffect, useRef } from "react";
import { MdPlayArrow } from "react-icons/md";
import ReactPlayer from "react-player/youtube";
import { getSession, removeSession } from "~/lib/helperFuncation/sessionStorageService";
import { changeIsPlayingState } from "~/lib/store/slice/audioSlice";
import {
  onPauseVideo,
  setCurrentVideo,
  setCurrentVideoPosition,
  setIsMute,
  setPipMode,
  setYoutubeVideoIsPlay,
} from "~/lib/store/slice/youtubePlayerSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { VIDEO_PLAYER, youtubeLinkPrefix } from "~/utils/constant";
import { isIOS } from "~/utils/helper-funcations";
import ClientOnly from "./ClentOnly";

function YoutubeVideoPlayer({
  videoId,
  handleNextVideo,
  controls = true,
  handlePlayVideo,
  onReady,
}: {
  videoId: string;
  handlePlayVideo: () => void;
  handleNextVideo?: () => void;
  controls?: boolean;
  onReady?: () => void;
}) {
  const dispatch = useAppDispatch();
  const playerRef = useRef<ReactPlayer | null>(null);
  // const [isMuted] = useState<boolean>(isIOS());

  const { isVideoPlay, currentVideoPosition, currentVideoId, isMute } = useAppSelector((state) => state.youtubeVideo);

  const handleReady = async () => {
    if (!isVideoPlay) {
      dispatch(onPauseVideo());
    } else {
      const savedPosition = getSession(VIDEO_PLAYER.CURRENT_VIDEO_POSITION);
      if (playerRef.current && savedPosition && savedPosition > 0) {
        await playerRef.current.seekTo(savedPosition, "seconds");
      }
    }
  };

  const handleVideoEnd = async () => {
    // Reset position to 0 for next video
    dispatch(setCurrentVideoPosition(0));
    removeSession(VIDEO_PLAYER.CURRENT_VIDEO_POSITION);
    handleNextVideo && handleNextVideo();
    await playerRef.current?.seekTo(0, "seconds");
  };

  // const handlePlayClick = () => {
  //   dispatch(setYoutubeVideoIsPlay(true));
  //   dispatch(changeIsPlayingState(false));
  //   dispatch(setPipMode(true));
  //   handlePlayVideo();
  // };

  const handlePlayClick = () => {
    if (!isVideoPlay) {
      dispatch(setYoutubeVideoIsPlay(true));
    }
    dispatch(changeIsPlayingState(false));
    dispatch(setPipMode(true));
    handlePlayVideo();
  };

  useEffect(() => {
    // dispatch(setCurrentVideo(getSession(VIDEO_PLAYER.CURRENT_VIDEO) || null));
    if (!currentVideoId) {
      dispatch(setCurrentVideo(getSession(VIDEO_PLAYER.CURRENT_VIDEO) || null));
    }
    dispatch(setIsMute(isIOS()));

    // Restore position after a short delay to ensure player is ready
    const timer = setTimeout(() => {
      const savedPosition = getSession(VIDEO_PLAYER.CURRENT_VIDEO_POSITION);
      if (playerRef.current && savedPosition && savedPosition > 0) {
        playerRef.current.seekTo(savedPosition, "seconds");
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [dispatch]);

  // Reset position to 0 when a new video is selected
  useEffect(() => {
    if (videoId && videoId !== currentVideoId) {
      removeSession(VIDEO_PLAYER.CURRENT_VIDEO_POSITION);
      if (playerRef.current) {
        playerRef.current.seekTo(0, "seconds");
      }
    }
  }, [videoId, currentVideoId]);

  return (
    <ClientOnly
      fallback={
        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center rounded-lg relative">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center animate-pulse">
              <MdPlayArrow className="w-8 h-8 text-white" />
            </div>
            <p className="text-gray-400 text-sm font-medium">Loading player...</p>
          </div>
        </div>
      }
    >
      <ReactPlayer
        key={videoId}
        ref={playerRef}
        url={`${youtubeLinkPrefix}${videoId}`}
        width="100%"
        height="100%"
        playing={isVideoPlay}
        controls={controls}
        style={{ pointerEvents: "auto" }}
        onPlay={handlePlayClick}
        onPause={() => {
          if (!playerRef.current) return;

          const internal = playerRef.current.getInternalPlayer();
          if (internal && internal.getPlayerState && internal.getPlayerState() === 2) {
            dispatch(setYoutubeVideoIsPlay(false));
            dispatch(onPauseVideo());
          }
        }}
        onProgress={(progress) => {
          dispatch(setCurrentVideoPosition(progress.playedSeconds));
        }}
        onReady={handleReady}
        onEnded={handleVideoEnd}
        onError={(error) => {
          console.error("YouTube player error:", error);
          dispatch(setYoutubeVideoIsPlay(false));
          dispatch(onPauseVideo());
        }}
      />
    </ClientOnly>
  );
}

export default YoutubeVideoPlayer;
